import { faro } from "@grafana/faro-web-sdk";
import { Classification, classifyError, FailureDetails } from "./classifyError";

const toError = (error: unknown): Error =>
  error instanceof Error ? error : new Error(String(error));

// Faro requires string values for event attributes and error context.
function toAttributes(
  values: Record<string, string | number | boolean | undefined>
): Record<string, string> {
  const attributes: Record<string, string> = {};
  Object.entries(values).forEach(([key, value]) => {
    if (value !== undefined) {
      attributes[key] = String(value);
    }
  });
  return attributes;
}

// Includes the operation so telemetry groups failures per operation.
function describeFailure(error: Error, details: FailureDetails): string {
  const where = [details.endpoint, details.operation]
    .filter((part) => part !== undefined)
    .join(" · ");

  if (details.unparseable) {
    return `Unparseable response · ${where}`;
  }

  if (details.status !== undefined) {
    return `HTTP ${details.status} · ${where}`;
  }

  return `${error.message} · ${where}`;
}

/**
 * The single entry point for sending an error to telemetry. Classifies the
 * error, then reports it, counts it, or drops it. Returns the classification so
 * callers can react to it (e.g. choose a notification).
 *
 * Deliberately never writes to console.error: Faro's console instrumentation
 * would capture that as a second, unclassified error.
 */
export function reportError(error: unknown): Classification {
  const classification = classifyError(error);
  const api = faro?.api;

  if (!api) {
    return classification;
  }

  switch (classification.kind) {
    case "server-5xx": {
      const { status, operation, endpoint, url } = classification.details;
      api.pushEvent(
        "http_server_error",
        toAttributes({ status, operation, endpoint, url })
      );
      break;
    }

    case "reportable": {
      const original = toError(error);
      const { details } = classification;

      if (!details) {
        api.pushError(original);
        break;
      }

      const { operation, endpoint, url, status, tokenExpired, callStack } =
        details;
      const reported = new Error(describeFailure(original, details));
      if (callStack) {
        // Faro builds the report's stack trace from this; without it the trace
        // would lead here rather than to the code that made the request.
        reported.stack = callStack;
      }

      api.pushError(reported, {
        type: original.name,
        context: toAttributes({
          operation,
          endpoint,
          url,
          status,
          tokenExpired,
        }),
      });
      break;
    }

    default:
      // network, server-500, unauthorized-expired: not reported.
      break;
  }

  return classification;
}
